import { Image } from "lucide-react";
import { useCallback } from "react";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/use-toast";
import type * as spine from "@esotericsoftware/spine-webgl";
import type { SpinePreviewApp } from "../../../../lib/SpinePreviewApp";
import { useConfigStore } from "../../../../store/configStore";
import { useEditorStore } from "../../../../store/editorStore";
import type { StaticStackItem } from "../../../../lib/staticLayerStack";
import {
  ANIMATION_EXPORT_DEFAULTS,
  ANIMATION_EXPORT_VARIANTS,
  type AnimationExportFormat,
  type AnimationExportSize,
  type AnimationExportVariant,
} from "./exportAnimationConfig";
import { exportGif } from "./utils/exportGif";
import { exportWebp } from "./utils/exportWebp";

export type { AnimationExportFormat, AnimationExportSize, AnimationExportVariant };
export {
  ANIMATION_EXPORT_DEFAULTS,
  ANIMATION_EXPORT_VARIANTS,
} from "./exportAnimationConfig";

export interface ExportSpineAnimationBlobOptions {
  format: AnimationExportFormat;
  outputSize: AnimationExportSize | number;
  /** 无 Zip 纯静态模版时可为 null，此时需有 {@link staticStackItems} 中的 img 层 */
  app: SpinePreviewApp | null | undefined;
  spineCanvas: spine.SpineCanvas | null | undefined;
  staticStackItems?: StaticStackItem[];
  /** WebP 每帧编码质量，默认 0.9 */
  quality?: number;
  /** gif.js 编码质量，1 最高 */
  gifQuality?: number;
  /** WebP 最大帧数，在整段时长内均匀采样 */
  maxFrames?: number;
  onProgress: (percent: number) => void;
}

/** 导出 GIF / Animated WebP（固定透明背景；不含下载与 toast）。 */
export function exportSpineAnimationBlob(
  options: ExportSpineAnimationBlobOptions,
): Promise<Blob> {
  const {
    format,
    outputSize,
    app,
    spineCanvas,
    staticStackItems,
    quality = ANIMATION_EXPORT_DEFAULTS.quality,
    gifQuality = ANIMATION_EXPORT_DEFAULTS.gifQuality,
    maxFrames,
    onProgress,
  } = options;

  const shared = {
    app,
    spineCanvas,
    outputSize,
    transparentBackground: ANIMATION_EXPORT_DEFAULTS.transparentBackground,
    highQualityCapture: ANIMATION_EXPORT_DEFAULTS.highQualityCapture,
    onProgress,
    staticStackItems,
  };

  if (format === "webp") {
    return exportWebp({ ...shared, quality, maxFrames });
  }

  return exportGif({ ...shared, gifQuality });
}

/** 由父组件在 Dropdown 外持有，用于挂载 ExportAnimationProgressDialog */
export interface ExportAnimationDialogState {
  open: boolean;
  setOpen: (open: boolean) => void;
  progress: number;
  setProgress: (progress: number) => void;
  format: AnimationExportFormat;
  setFormat: (format: AnimationExportFormat) => void;
}

export interface ExportAnimationMenuSubProps {
  spineApp: SpinePreviewApp | null;
  spineCanvas: spine.SpineCanvas | null;
  templateId: string | undefined;
  dialog: ExportAnimationDialogState;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function ExportAnimationMenuSub({
  spineApp,
  spineCanvas,
  templateId,
  dialog,
}: ExportAnimationMenuSubProps) {
  const { setOpen, setProgress, setFormat } = dialog;
  const templateConfig = useConfigStore((s) => s.templateConfig);
  /** 与 SpinePreview 中 mergedStaticStack 同步，见 editorStore */
  const previewMergedStaticStack = useEditorStore(
    (s) => s.previewMergedStaticStack,
  );

  const handleExport = useCallback(
    async (variant: AnimationExportVariant) => {
      const hasSpine =
        !!spineApp &&
        !!spineCanvas &&
        spineApp.instances.length > 0;
      const hasStaticImg =
        !!previewMergedStaticStack?.some((i) => i.kind === "img");

      if (!hasSpine && !hasStaticImg) {
        toast({
          variant: "destructive",
          title: "导出失败",
          description: "预览尚未初始化或没有可导出的静态图层",
        });
        return;
      }

      setFormat(variant.format);
      setOpen(true);
      setProgress(0);

      try {
        const blob = await exportSpineAnimationBlob({
          format: variant.format,
          outputSize: variant.size,
          app: spineApp,
          spineCanvas,
          staticStackItems: previewMergedStaticStack,
          onProgress: setProgress,
        });

        downloadBlob(
          blob,
          `avatar-${templateId || "preview"}${variant.fileSuffix}.${variant.format}`,
        );

        toast({
          title: "导出成功",
          description: `${variant.label} 已下载（${variant.size}×${variant.size}，透明背景）`,
        });
      } catch (err) {
        toast({
          variant: "destructive",
          title: `${variant.label}失败`,
          description: err instanceof Error ? err.message : "未知错误",
        });
      } finally {
        setOpen(false);
        setProgress(0);
      }
    },
    [
      spineApp,
      spineCanvas,
      previewMergedStaticStack,
      templateId,
      setOpen,
      setProgress,
      setFormat,
    ],
  );

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={!templateConfig || dialog.open}>
        <Image className="h-4 w-4 mr-2" />
        导出动图
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {ANIMATION_EXPORT_VARIANTS.map((variant) => (
          <DropdownMenuItem
            key={variant.id}
            onClick={() => handleExport(variant)}
          >
            {variant.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
